import React from 'react';

import { useGetUser } from '@vlabs/api/artemy/client';
import type { UserId } from '@vlabs/api/artemy/model';
import { BlockUserButton, UnblockUserButton } from '@vlabs/monokit/features';
import { PageHeader } from '@vlabs/monokit/shared/ui/page';
import { withPermissions, usePageAccess } from '@vlabs/permissions';
import { Breadcrumb, Breadcrumbs } from '@vlabs/uikit';
import { routes } from '@vlapps/point/src/app/routes';
import { generatePath } from 'react-router-dom';

import { useUserDetailsPageTranslation } from '../i18n';
import { UserStatusTag } from '../user-status-tag/UserStatusTag';

const BlockUserWithAccess = withPermissions('users', 'update')(BlockUserButton);
const UnblockUserWithAccess = withPermissions('users', 'update')(UnblockUserButton);

interface UserDetailsPageHeaderProps {
  userId: UserId;
}

function UserDetailsPageHeader({
  userId,
}: UserDetailsPageHeaderProps): React.JSX.Element {
  const { t } = useUserDetailsPageTranslation();
  const { data: user } = useGetUser(userId);
  const canViewUserList = usePageAccess('users');

  const actions = (() => {
    if (!user?.status) return null;
    if (user.status === 'pending') {
      return (
        <UnblockUserWithAccess
          userId={userId}
          variant="activate"
        />
      );
    }
    return user.status === 'blocked'
      ? <UnblockUserWithAccess userId={userId} />
      : <BlockUserWithAccess userId={userId} />;
  })();

  return (
    <PageHeader
      actions={actions}
      breadcrumbs={(
        <Breadcrumbs>
          <Breadcrumb
            caption={t('user-details-page:breadcrumb')}
            href={canViewUserList ? generatePath(routes.users.list) : undefined}
          />
          <Breadcrumb caption={user?.email ?? ''} />
        </Breadcrumbs>
      )}
      tag={<UserStatusTag status={user?.status} />}
      title={user?.email}
    />
  );
}

export { UserDetailsPageHeader };
